<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Card, Table, Button, Spin, Tag } from 'ant-design-vue';
import { ArrowLeftOutlined, FileTextOutlined, RightOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { getConsultantClient } from '@/api/consultation-workspace/consultationWorkspaceApi';
import {
    formatConsultationTimeRange,
    getConsultationBookingStatusMeta
} from '@/utils/consultationBookingStatus';

const route = useRoute();
const router = useRouter();
const userId = computed(() => route.params.userId);
const loading = ref(false);
const user = ref(null);
const consultations = ref([]);

const columns = [
    { title: '時段', key: 'starts_at', width: 200 },
    { title: '模式', dataIndex: 'mode', key: 'mode', width: 90 },
    { title: '申請版本', key: 'version_name', width: 240 },
    { title: '諮詢狀態', dataIndex: 'status', key: 'status', width: 110 }
];

const load = async () => {
    loading.value = true;
    try {
        const res = await getConsultantClient(userId.value);
        if (res.status === 1) {
            user.value = res.data.user;
            consultations.value = (res.data.consultations || []).map((c) => ({
                ...c,
                key: c.id
            }));
        }
    } finally {
        loading.value = false;
    }
};

const openVersion = (versionId) => {
    if (!versionId) return;
    router.push({
        name: 'ConsultationWorkspaceVersionView',
        params: { id: versionId },
        query: { readonly: '1' }
    });
};

const formatVersionTime = (value) => {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : String(value);
};

onMounted(load);
</script>

<template>
    <Spin :spinning="loading">
        <div class="cw-history">
            <header class="cw-history-topbar">
                <div class="cw-history-topbar__nav">
                    <button
                        type="button"
                        class="cw-history-topbar__back"
                        @click="router.push(`/consultation-workspace/clients/${userId}`)"
                    >
                        <ArrowLeftOutlined />
                        學員詳情
                    </button>
                    <span class="cw-history-topbar__sep">/</span>
                    <span class="cw-history-topbar__title">諮詢紀錄</span>
                    <Tag v-if="user?.name" class="cw-history-topbar__tag">{{ user.name }}</Tag>
                </div>
            </header>

            <Card class="admin-panel" :bordered="false">
            <Table :columns="columns" :data-source="consultations" :pagination="false">
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'starts_at'">
                        {{ formatConsultationTimeRange(record.starts_at, record.ends_at) }}
                    </template>
                    <template v-else-if="column.key === 'mode'">
                        {{ record.mode === 'offline' ? '實體' : '線上' }}
                    </template>
                    <template v-else-if="column.key === 'version_name'">
                        <button
                            v-if="record.version_id"
                            type="button"
                            class="cw-version-chip"
                            @click="openVersion(record.version_id)"
                        >
                            <FileTextOutlined class="cw-version-chip__icon" />
                            <span class="cw-version-chip__body">
                                <span class="cw-version-chip__name">{{
                                    record.version_name || '版本'
                                }}</span>
                                <span
                                    v-if="record.version_updated_at"
                                    class="cw-version-chip__time"
                                >
                                    {{ formatVersionTime(record.version_updated_at) }}
                                </span>
                            </span>
                            <RightOutlined class="cw-version-chip__arrow" />
                        </button>
                        <span v-else class="text-muted">—</span>
                    </template>
                    <template v-else-if="column.key === 'status'">
                        <Tag :color="getConsultationBookingStatusMeta(record.status).color">
                            {{ getConsultationBookingStatusMeta(record.status).label }}
                        </Tag>
                    </template>
                </template>
            </Table>
            </Card>
        </div>
    </Spin>
</template>

<style scoped>
.cw-history-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
}
.cw-history-topbar__nav {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}
.cw-history-topbar__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #8b6914;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.cw-history-topbar__back:hover {
    color: #6b5428;
}
.cw-history-topbar__sep {
    color: #cbd5e1;
    font-size: 12px;
}
.cw-history-topbar__title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}
.cw-history-topbar__tag {
    margin: 0;
    border-radius: 999px;
    font-weight: 600;
}

.text-muted {
    color: rgba(0, 0, 0, 0.45);
}

.cw-version-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    padding: 6px 10px;
    border-radius: 10px;
    border: 1px solid #e8e2d6;
    background: linear-gradient(180deg, #fff 0%, #faf7f1 100%);
    cursor: pointer;
    text-align: left;
    transition:
        border-color 0.15s,
        box-shadow 0.15s;
}

.cw-version-chip:hover {
    border-color: rgba(196, 163, 90, 0.65);
    box-shadow: 0 4px 12px rgba(196, 163, 90, 0.12);
}

.cw-version-chip__icon {
    flex-shrink: 0;
    color: #8b6914;
    font-size: 14px;
}

.cw-version-chip__body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cw-version-chip__name {
    font-size: 13px;
    font-weight: 600;
    color: #272b36;
}

.cw-version-chip__time {
    font-size: 11px;
    color: #8c8f96;
}

.cw-version-chip__arrow {
    flex-shrink: 0;
    font-size: 10px;
    color: #b8bcc4;
}
</style>
